<template>
  <page :name="$t('Utenti dell\'azienda')" :state="[invitesState, usersState]" hide-breadcrumbs>
    <pf-page-section no-body-wrapper>
      <pf-card>
        <pf-card-header>
          <pf-card-title>
            <pf-title h="3">{{ $t('Invia nuovo invito di accesso') }}</pf-title>
            <p>
              {{ $t("Inserisci un indirizzo email valido e specifica i permessi e le notifiche email che verranno inviate all'utente.") }}
              <pf-popover auto-width position="auto" :placement-options="{ allowedPlacements: ['bottom', 'top', 'bottom-start', 'top-start', 'bottom-end', 'top-end'] }">
                <pf-button variant="link" inline @click="showLegend = true">{{ $t('Leggi la legenda dei permessi') }}</pf-button>

                <template #header>
                  {{ $t('Legenda permessi utente') }}
                </template>
                <template #body>
                  <user-legend />
                </template>
              </pf-popover>
            </p>
          </pf-card-title>
        </pf-card-header>
        <pf-card-body>
          <form-user-access
            v-model="invite"
            ouia-id="form-send-new-invite"
            :disabled="!inviteEmail || sending"
            :submit-label="$t('Invia invito')"
            @update:model-value="sendInvite"
          >
            <form-group :name="$t('Email (utilizzata anche come username)')" style="max-width: 650px">
              <x-input v-model="inviteEmail" type="email" name="email" maxlength="100" />
            </form-group>
          </form-user-access>
        </pf-card-body>
      </pf-card>

      <pf-title h="2" class="pf-v6-u-mt-lg">{{ $t('Utenti collegati') }}</pf-title>

      <pf-data-list>
        <pf-data-list-item v-for="row in members" :key="row.id" :expandable="row.type === 'user'" :ouia-id="`user-id-${row.mail}-list-item`">
          <pf-data-list-cell no-fill>
            <paper-plane-icon v-if="row.type === 'invite'" />
            <pf-label v-else-if="row.is_self" color="blue">
              <template #icon>
                <user-icon />
              </template>
              {{ $t('Sei tu') }}
            </pf-label>
            <user-icon v-else />
          </pf-data-list-cell>

          <pf-data-list-cell>
            <div v-if="!row.firstname && !row.lastname" :title="row.mail">{{ row.mail }}</div>
            <template v-else>
              <div :title="`${row.firstname} ${row.lastname}`">{{ row.firstname }} {{ row.lastname }}</div>
              <div :title="row.mail">&lt;{{ row.mail }}&gt;</div>
            </template>
          </pf-data-list-cell>

          <pf-data-list-cell no-fill>
            <pf-title h="4">{{ $t('Permessi attivi') }}: </pf-title>
            <pf-label v-if="row.is_owner" color="green">
              <template #icon>
                <crow-icon />
              </template>
              {{ $t('Proprietario') }}
            </pf-label>
            <pf-label v-else-if="row.is_admin" color="orange">
              <template #icon>
                <user-tie-icon />
              </template>
              {{ $t('Amministratore') }}
            </pf-label>
            <template v-else-if="row.grants.length">
              <pf-label v-if="row.grants.includes('developer')">{{ $t('tecnici') }}</pf-label>
              <pf-label v-if="row.grants.includes('sales')">{{ $t('commerciali') }}</pf-label>
              <pf-label v-if="row.grants.includes('billing')">{{ $t('amministrative') }}</pf-label>
            </template>
            <template v-else>{{ $t('Nessuno') }}</template>
          </pf-data-list-cell>
          <pf-data-list-cell>
            <pf-title h="4">{{ $t('Notifiche attive') }}: </pf-title>
            <template v-if="row.emailSubscriptions.length">
              <pf-label v-if="row.emailSubscriptions.includes('technical')">{{ $t('tecniche') }}</pf-label>
              <pf-label v-if="row.emailSubscriptions.includes('marketing')">{{ $t('commerciali') }}</pf-label>
              <pf-label v-if="row.emailSubscriptions.includes('billing')">{{ $t('amministrative') }}</pf-label>
            </template>
            <template v-else>{{ $t('Nessuna') }}</template>
          </pf-data-list-cell>

          <pf-data-list-action>
            <template v-if="row.type == 'invite'">
              <pf-button small @click.prevent="resendInvite(row.mail)">
                <template #icon><paper-plane-icon /></template>
                <span class="pf-v6-u-display-none pf-v6-u-display-block-on-sm">{{ $t('Reinvia invito') }}</span>
              </pf-button>

              <pf-button variant="danger" small @click.prevent="deleteInvite(row.mail)" v-bind="{ title: $t('Annulla invito') }">
                <template #icon><trash-icon /></template>
                <span class="sr-only">{{ $t('Annulla invito') }}</span>
              </pf-button>
            </template>

            <pf-button v-else :disabled="row.is_owner" v-bind="{ title: $t('Rimuovi utente') }" variant="danger" small @click.prevent="deleteUser(row)">
              <template v-if="row.is_self" #icon> <power-off-icon /></template>
              <template v-else #icon><trash-icon /></template>

              <template v-if="row.is_self"> {{ $t('Lascia') }} </template>
              <span v-else class="sr-only">{{ $t('Rimuovi utente') }}</span>
            </pf-button>
          </pf-data-list-action>

          <pf-data-list-content>
            <form-user-access
              :ouia-id="`form-user-${row.mail}`"
              :model-value="row"
              :disabled="sending"
              :submit-label="$t('Aggiorna permessi e notifiche')"
              @update:model-value="updateUser(row, $event)"
            />
          </pf-data-list-content>
        </pf-data-list-item>
      </pf-data-list>
    </pf-page-section>
  </page>
</template>

<script lang="ts" setup>
import { http } from '../http';
import FormUserAccess, { type FormUserData } from '../components/form-user-access.vue';
import { asyncComputedList } from '@common/asyncComputed';
import { useLoginStore } from '@/store/login';
import { useAppStore } from '@/store/app';
import { Resource } from '@/resources';
import UserLegend from '../components/user-legend.vue';
import { ref, computed } from 'vue';
import cloneDeep from 'lodash-es/cloneDeep';
import { $t as _t } from '@/i18n';
import { useRouter, useRoute } from 'vue-router';

import UserIcon from '@vue-patternfly/icons/user-icon';
import TrashIcon from '@vue-patternfly/icons/trash-icon';
import PowerOffIcon from '@vue-patternfly/icons/power-off-icon';
import PaperPlaneIcon from '@vue-patternfly/icons/paper-plane-icon';
import CrowIcon from '@vue-patternfly/icons/crown-icon';
import UserTieIcon from '@vue-patternfly/icons/user-tie-icon';

const inviteDataReset: FormUserData = {
  emailSubscriptions: [],
  grants: [],
};

type GrantMember = {
  id?: number;
  type: 'user' | 'invite';
  mail: string;
  firstname: string;
  lastname: string;
  is_owner: boolean;
  is_admin: boolean;
  is_self: boolean;
  grants: ('billing' | 'sales' | 'developer' | 'admin')[];
  emailSubscriptions: ('billing' | 'marketing' | 'technical')[];
};

const login = useLoginStore();
const router = useRouter();
const route = useRoute();

const { result: users, state: usersState } = asyncComputedList(() => new Resource('users').get());
const { result: invites, state: invitesState } = asyncComputedList(() => new Resource('invites').get());

const invite = ref(cloneDeep(inviteDataReset));
const inviteEmail = ref('');
const sending = ref(false);
const showLegend = ref(false);

const members = computed(() => {
  const members: GrantMember[] = [];
  for (const user of users.value) {
    members.push({
      id: user.id,
      type: 'user',
      mail: user.mail,
      firstname: user.firstname,
      lastname: user.lastname,
      is_owner: user.grants.includes('owner'),
      is_admin: user.grants.includes('admin'),
      is_self: user.username === login.username,
      grants: user.grants,
      emailSubscriptions: user.email_subscriptions,
    });
  }
  for (const invite of invites.value) {
    members.push({
      type: 'invite',
      mail: invite.email,
      firstname: invite.firstname,
      lastname: invite.lastname,
      is_owner: false,
      is_admin: invite.grants.includes('admin'),
      is_self: false,
      grants: invite.grants,
      emailSubscriptions: invite.email_subscriptions,
    });
  }
  return members;
});

async function sendInvite(userData: FormUserData | undefined) {
  if (sending.value || !userData || !window.confirm(_t("Inviare l'invito a {0}?", [inviteEmail.value]))) {
    return;
  }

  sending.value = true;
  try {
    await new Resource('invites').post({
      email: inviteEmail.value,
      grants: userData.grants,
      email_subscriptions: userData.emailSubscriptions,
    });
  } finally {
    sending.value = false;
  }

  invite.value = cloneDeep(inviteDataReset);
  inviteEmail.value = '';

  invitesState.update();
}

async function resendInvite(email: string) {
  if (sending.value || !window.confirm(_t("Reinviare l'invito a {0}?", [email]))) {
    return;
  }

  sending.value = true;
  try {
    await new Resource('invites').post({
      action: 'resend',
      email,
    });
  } finally {
    sending.value = false;
  }
}

async function deleteInvite(email: string) {
  if (sending.value || !window.confirm(_t("Annullare l'invito a {0}?", [email]))) {
    return;
  }

  sending.value = true;
  try {
    await new Resource('invites').delete(email);
    invitesState.update();
  } finally {
    sending.value = false;
  }
}

async function updateUser(row: GrantMember, userData: FormUserData | undefined) {
  if (sending.value) {
    return;
  }

  const res = row.type === 'invite' ? 'invites' : 'users';
  const id = res === 'invites' ? row.mail : row.id;

  if (!id || !userData) {
    throw new Error('Missing member id/email');
  }

  sending.value = true;
  try {
    await new Resource(res).put(id, {
      grants: userData.grants,
      email_subscriptions: userData.emailSubscriptions,
    });
  } finally {
    sending.value = false;
  }

  if (res === 'users') {
    usersState.update();
  } else {
    invitesState.update();
  }
}

async function deleteUser(row: GrantMember) {
  if (sending.value || !row.id || (login.grants.owner && row.id == login.id)) {
    return;
  }

  const res = row.type === 'invite' ? 'invites' : 'users';

  if (!window.confirm(_t('Rimuovere questa utenza?'))) {
    return;
  }

  sending.value = true;
  try {
    await new Resource(res).delete(row.id);
  } finally {
    sending.value = false;
  }

  if (res === 'users' && row.id == login.id) {
    await http.post('/api/auth/logout');

    const app = useAppStore();
    app.logout();

    router.push({
      name: 'login',
      query: {
        ref: route.fullPath,
      },
    });
  } else if (res === 'users') {
    usersState.update();
  } else {
    invitesState.update();
  }
}
</script>
